import { ApiError } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { uuidv7 } from "@/lib/uuid";
import { dispatchActions, type BillDispatch } from "./dispatchBoard";

/**
 * Offering bags to a driver (driver backend B3 · P13, B6 · P24 plan D-38),
 * shared by the one-bag sheet (`AssignDriverSheet`) and the Delivery tab's
 * many-bag panel (`AssignDriversPanel`).
 *
 * One existing T2 call per bag — `POST dispatch/assignments`, or the reassign
 * for a bag whose offer is still out — each with its own client UUIDv7 `id`
 * (D-10). An answer that was lost (network failure) keeps its id, so the retry
 * cannot offer the bag twice; a definite answer — success or a typed refusal —
 * retires it.
 */

/** The till refusals an assign can meet (plan D-32), each in the till's words. */
export const ASSIGN_REFUSALS = {
  DISPATCH_DRIVER_NOT_AVAILABLE: "errorNotAvailable",
  DISPATCH_INVOICE_ALREADY_ASSIGNED: "errorAlreadyAssigned",
  DISPATCH_INVOICE_NOT_DELIVERABLE: "errorNotDeliverable",
  DISPATCH_INVALID_TRANSITION: "errorInvalidTransition",
  DISPATCH_ASSIGNMENT_NOT_FOUND: "errorNotFound",
  DISPATCH_COMMAND_ID_CONFLICT: "errorConflict",
} as const;

export type AssignRefusalKey = (typeof ASSIGN_REFUSALS)[keyof typeof ASSIGN_REFUSALS];

/** The `dispatch` message key for a refusal code, or null for anything else. */
export function refusalKey(code: string | undefined | null): AssignRefusalKey | null {
  if (!code) return null;
  return (ASSIGN_REFUSALS as Record<string, AssignRefusalKey>)[code] ?? null;
}

/** One command to send: the retry key, the route and the body (without id/timestamp). */
export type AssignTarget = {
  key: string;
  path: string;
  body: Record<string, unknown>;
  kind: "assign" | "reassign";
};

/**
 * What offering this bag to this driver means right now: a reassign while its
 * offer or acceptance is live and before pickup, an assign while nothing holds
 * it, else nothing (past pickup only a problem resolution moves a bag).
 */
export function assignTarget(
  invoiceId: string,
  entry: BillDispatch | undefined,
  driverSellerId: string,
): AssignTarget | null {
  const current = entry?.current ?? null;
  const actions = dispatchActions(entry);
  if (current && actions.reassign) {
    return {
      key: `reassign:${current.id}:${driverSellerId}`,
      path: endpoints.restaurant.dispatchReassign(current.id),
      body: { driverSellerId },
      kind: "reassign",
    };
  }
  if (actions.assign) {
    return {
      key: `assign:${invoiceId}:${driverSellerId}`,
      path: endpoints.restaurant.dispatchAssignments,
      body: { invoiceId, driverSellerId },
      kind: "assign",
    };
  }
  return null;
}

/**
 * The client command ids, one per retry key. The same key asks again → the
 * same id (a lost answer is retried safely); a definite answer retires it.
 */
export class CommandIds {
  private readonly ids = new Map<string, string>();

  idFor(key: string): string {
    let id = this.ids.get(key);
    if (!id) {
      id = uuidv7();
      this.ids.set(key, id);
    }
    return id;
  }

  retire(key: string): void {
    this.ids.delete(key);
  }

  has(key: string): boolean {
    return this.ids.has(key);
  }
}

/** One bag's result. */
export type AssignOutcome =
  | { invoiceId: string; status: "offered" }
  /** A typed refusal from the server; `key` names its message when known. */
  | { invoiceId: string; status: "refused"; code: string | null; key: AssignRefusalKey | null; error: unknown }
  /** No definite answer (network): the id is kept for the retry. */
  | { invoiceId: string; status: "failed"; error: unknown }
  /** Nothing to send: the bag can no longer be offered (picked up meanwhile). */
  | { invoiceId: string; status: "skipped" };

export type AssignSend = (path: string, body: Record<string, unknown>) => Promise<unknown>;

/**
 * Offers each bag to the driver, ONE AFTER ANOTHER — never in parallel, so the
 * server meets them in the order the cashier sees and a driver who becomes
 * unavailable halfway refuses the rest cleanly. Every bag gets its own answer.
 */
export async function assignBills(
  bills: readonly { invoiceId: string; entry: BillDispatch | undefined }[],
  driverSellerId: string,
  send: AssignSend,
  ids: CommandIds,
  clock: () => string = () => new Date().toISOString(),
): Promise<AssignOutcome[]> {
  const outcomes: AssignOutcome[] = [];
  for (const { invoiceId, entry } of bills) {
    const target = assignTarget(invoiceId, entry, driverSellerId);
    if (!target) {
      outcomes.push({ invoiceId, status: "skipped" });
      continue;
    }
    try {
      await send(target.path, { id: ids.idFor(target.key), clientTimestamp: clock(), ...target.body });
      ids.retire(target.key);
      outcomes.push({ invoiceId, status: "offered" });
    } catch (error) {
      if (error instanceof ApiError) {
        ids.retire(target.key);
        outcomes.push({
          invoiceId,
          status: "refused",
          code: error.code ?? null,
          key: refusalKey(error.code),
          error,
        });
      } else {
        outcomes.push({ invoiceId, status: "failed", error });
      }
    }
  }
  return outcomes;
}
